import { Component, OnInit, ViewChild, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

// components
import { AuthenticationComponent } from '../../shared/components/authentication/authentication.component';
import { EditProfileComponent } from '../../shared/components/edit-profile/edit-profile.component';
import { EditUserNotificationsComponent } from '../../shared/components/edit-user-notifications/edit-user-notifications.component';

// models
import { ApplicationSettings } from '../models/application-settings';
import { AuctionCluster } from '../models/auction-cluster';
import { BuyerLevel } from '../models/buyer-level';
import { Language } from '../models/language';
import { MFATypeEnum } from '../models/mfaType';
import { SupplierLevel } from '../models/supplier-level';
import { UserPermissions } from '../models/user-permissions';

// services
import { ServiceFactory } from '../factory/service-factory';
import { AuctionClusterService } from '../../platform/shared/services/auction-cluster.service';
import { AuthService } from '../services/auth.service';
import { BuyerService } from '../services/buyer.service';
import { DynamicRoutingService } from '../services/dynamic-routing.service';
import { ErrorService } from '../services/error.service';
import { LanguagePreferenceService } from '../services/language-preference.service';
import { LanguageService } from '../services/language.service';
import { SupplierService } from '../services/supplier.service';
import { TokenService } from '../services/token.service';
import { TitleService } from '../services/title.service';
import { UserService } from '../services/user.service';
import { WebApiService } from '../services/web-api.service';


@Component({
  selector: 'header-component',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HeaderComponent implements OnInit, OnDestroy {

  translations: any; // make readonly
  languages: Array<Language> = [];
  currentLanguage: Language = new Language();
  appModeDialogOpened: boolean;

  auctionClusters: Array<AuctionCluster>;
  auctionClusterId = 0;

  buyers: Array<BuyerLevel>;
  buyerId = 0;

  suppliers: Array<SupplierLevel>;
  supplierId = 0;

  permissions: UserPermissions;
  appModeEnabled: boolean;
  isPlatformUser: boolean;
  isAuctionClusterUser: boolean;
  isBuyerUser: boolean;
  isSupplierUser: boolean;
  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;

  @ViewChild(AuthenticationComponent) authComponent: AuthenticationComponent;
  @ViewChild(EditProfileComponent) epComponent: EditProfileComponent;
  @ViewChild(EditUserNotificationsComponent) epnComponent: EditUserNotificationsComponent;

  constructor(
    public authenticationService: AuthService,
    protected translate: TranslateService,
    private router: Router,
    private auctionClusterService: AuctionClusterService,
    private buyerService: BuyerService,
    private supplierService: SupplierService,
    private languageService: LanguageService,
    private errorService: ErrorService,
    private tokenService: TokenService,
    private dynamicRoutingService: DynamicRoutingService,
    public title: TitleService,
    public languagePreferenceService: LanguagePreferenceService,
    public userService: UserService,
    private activatedRoute: ActivatedRoute,
    protected appSettings: ApplicationSettings,
    protected webApiService: WebApiService
  ) {   

    this._subscription = this.languageService.direction.subscribe(res => {
      this.rtlEnabled = res;
    });

  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  isLevelUser(level: Array<any>): boolean {
    return level.length > 0;
  }

  ngOnInit() {
    this.translate.get('HEADER').subscribe((res: string) => {
      this.translations = res;
    });

    this.translate.onLangChange.subscribe(() => {
      this.translate.get('HEADER').subscribe((res: string) => {
        this.translations = res;
      });
    });

    this.appModeDialogOpened = false;

    this.permissions = this.tokenService.getCurrentUserPermissions();

    this.isPlatformUser = this.isLevelUser(this.permissions.PlatformPermissions);
    this.isAuctionClusterUser = this.isLevelUser(this.permissions.AuctionClusterPermissions)
      || this.isLevelUser(this.permissions.AuctionPermissions);
    this.isBuyerUser = this.isLevelUser(this.permissions.BuyerPermissions);
    this.isSupplierUser = this.isLevelUser(this.permissions.SupplierPermissions);

    let levelsAllowed = 0;
    // tslint:disable:curly
    if (this.isPlatformUser) levelsAllowed++;
    if (this.isAuctionClusterUser) levelsAllowed++;
    if (this.isBuyerUser) levelsAllowed++;
    if (this.isSupplierUser) levelsAllowed++;
    // tslint:enable:curly

    this.appModeEnabled = levelsAllowed > 1;

    // tslint:disable:no-magic-numbers
    forkJoin(
      this.languageService.getLanguages(),
      this.buyerService.getUserLevelBuyers(),
      this.supplierService.getUserLevelSuppliers()
    ).subscribe(result => {
      this.languages = result[0];
      this.currentLanguage = this.languages.find(lang => lang.code === this.languagePreferenceService.current());

      if (this.appModeEnabled && this.isAuctionClusterUser) {
        forkJoin(
          this.auctionClusterService.getAuctionClusters()
        ).subscribe(result => {
          this.auctionClusters = result[0].filter(f =>
            this.permissions.AuctionClusterPermissions.findIndex(p => p.Key === f.auctionClusterId) > -1 || this.permissions.AuctionPermissions.some(p => f.auction.some(a => a.auctionId == p.Key)));
          this.auctionClusters.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
          this.auctionClusterId = this.auctionClusters[0].auctionClusterId;
        });
      }

      this.buyers = result[1];
      if (this.buyers.length !== 0) {
        this.buyerId = this.buyers[0].buyerId;
      }

      this.suppliers = result[2];
      if (this.suppliers.length !== 0) {
        this.supplierId = this.suppliers[0].supplierId;
      }
    },
      error => {
        this.errorService.show(error);
      });
  }
  // tslint:enable:no-magic-numbers

  logout() {
    this.authenticationService.logout();
  }

  setLanguage(lang: string) {
    this.currentLanguage = this.languages.find(l => l.code === lang);
    this.languagePreferenceService.save(lang);
  }

  navigateToPlatformLevel() {
    this.dynamicRoutingService.getFirstAllowedRouteForLevel(this.permissions, 'platform').then(result => {
      this.router.navigate(['/platform/' + result]);
      this.appModeDialogOpened = false;
    });
  }

  navigateToAuctionClusterLevel() {
    this.dynamicRoutingService.getFirstAllowedRouteForLevel(this.permissions, 'auction', this.auctionClusterId).then(result => {
      this.router.navigate(['/auction/' + result]);
      this.appModeDialogOpened = false; 
    });
  }

  navigateToBuyerLevel() {
    this.dynamicRoutingService.getFirstAllowedRouteForLevel(this.permissions, 'buyer', this.buyerId).then(result => {
      this.router.navigate(['/buyer/' + result]);
      this.appModeDialogOpened = false;
    });
  }

  navigateToSupplierLevel() {
    this.dynamicRoutingService.getFirstAllowedRouteForLevel(this.permissions, 'supplier', this.supplierId).then(result => {
      this.router.navigate(['/supplier/' + result]);
      this.appModeDialogOpened = false;
    })
  }

  curentMode() {
    const url = document.location.hash;

    if (this.translations) {
      if (url.indexOf('platform') > -1) {
        return '(' + this.translate.instant('SHARED.PLATFORM') + ')';
      }
      if (url.indexOf('auction') > -1) {
        return '(' + this.translate.instant('SHARED.AUCTION_CLUSTER') + ')';
      }
      if (url.indexOf('buyer') > -1) {
        return '(' + this.translate.instant('SHARED.BUYER') + ')';
      }
      if (url.indexOf('supplier') > -1) {
        return '(' + this.translate.instant('SHARED.SUPPLIER') + ')';
      }
    }

    return '';
  }

  openAuthentication(userId: number, event: Event) {
    event.stopPropagation();
    let currentAuctionCluster = null;

    forkJoin(
      ServiceFactory.getAuctionClusterService(document.location.hash, this.activatedRoute, this.appSettings, this.webApiService).getAuctionClustersPerLevel(),
      this.userService.getCurrentUser()
    ).subscribe(result => {
      currentAuctionCluster = result[0][0] ? result[0][0] : null;
      let user = result[1];
      let isMFAEnabledOnCluster = (currentAuctionCluster === null || currentAuctionCluster === undefined) ? false : currentAuctionCluster.mfaTypeId !== MFATypeEnum.None;
      this.authComponent.open(user.userId, currentAuctionCluster ? currentAuctionCluster.auctionClusterId : this.auctionClusterId, user.mfaEnabled && isMFAEnabledOnCluster, true);
    });
  }

  editProfile(userId: number, event: Event) {
    event.stopPropagation();
    this.epComponent.open(userId, this.languages);
  }

  editNotifications(userId: number, event: Event) {
    event.stopPropagation();
    this.epnComponent.open(userId, this.languages);
  }
}
